/**
 * Standalone-Login-Pfad für UI-Design-Reviews außerhalb des Vario-iframes.
 *
 * v3 (2026-05-23): No-Auth-Bypass entfernt. Login-Form ist Pflicht.
 * User authentifiziert sich mit Email + Passwort gegen Supabase Auth,
 * dann tauschen wir das Supabase-Auth-JWT gegen einen Tenant-JWT
 * (mit `active_tenant_id`-Claim) per Edge-Function `dev-tenant-bridge`.
 *
 * Voraussetzungen DB-seitig:
 *   - User in `auth.users` mit Email `@goe-connect.de` oder `@go-ecommerce.de`.
 *   - Membership in `account.memberships` für den Ziel-Tenant.
 */

import { createClient } from '@supabase/supabase-js';
import type { AuthBridgeSession } from './auth-bridge-client.js';

export interface StandaloneLoginCtx {
  contentEl: HTMLElement;
  supabaseUrl: string;
  supabaseAnonKey: string;
}

interface DevBridgeResponse {
  token: string;
  expiresAt: string;
  tenantId: string;
  erpConnectionId: string | null;
  marketplaceKind: string;
  memberships: Array<{ tenantId: string; role: string | null; isDefault: boolean }>;
  mode?: 'authenticated';
  error?: { code: string; message: string };
}

/** Detection: kein Vario-Token in URL + nicht im iframe → Standalone. */
export function isStandaloneMode(): boolean {
  const search = new URLSearchParams(window.location.search);
  if (search.get('appToken')) return false;
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  if (hash.get('token')) return false;
  if (window.parent !== window) return false;
  return true;
}

export async function runStandaloneLogin(ctx: StandaloneLoginCtx): Promise<AuthBridgeSession> {
  return new Promise<AuthBridgeSession>((resolve) => {
    ctx.contentEl.hidden = false;
    ctx.contentEl.innerHTML = `
      <div class="kl-login-shell">
        <form class="kl-login-card" id="kl-login-form" autocomplete="on">
          <div class="kl-login-head">
            <img class="kl-brand-logo kl-login-logo" src="/logo-black.png" alt="go!e Commerce" width="835" height="338" />
            <div class="kl-login-sub">Kaufland · Standalone-Vorschau · Live-Daten</div>
          </div>
          <div class="kl-login-fields">
            <label class="kl-login-label">
              <span>E-Mail</span>
              <div class="v-input">
                <span class="v-icon fa-light fa-envelope"></span>
                <input type="email" name="email" required autocomplete="email" autofocus />
              </div>
            </label>
            <label class="kl-login-label">
              <span>Passwort</span>
              <div class="v-input">
                <span class="v-icon fa-light fa-lock"></span>
                <input type="password" name="password" required autocomplete="current-password" />
              </div>
            </label>
            <button type="submit" class="v-button v-button-primary kl-login-submit" id="kl-login-submit">
              <span class="v-icon fa-light fa-right-to-bracket" data-icon-default></span>
              Anmelden
            </button>
          </div>
          <div class="kl-login-msg" id="kl-login-msg"></div>
          <div class="kl-login-foot">
            Bypass-Login für UI-Design-Reviews mit Live-Daten aus der Supabase-DB.
            Echte App-Sessions laufen über den Vario-iframe-Slot.
          </div>
        </form>
      </div>
    `;

    const form = ctx.contentEl.querySelector<HTMLFormElement>('#kl-login-form')!;
    const msgEl = ctx.contentEl.querySelector<HTMLDivElement>('#kl-login-msg')!;
    const submitBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#kl-login-submit')!;
    const submitIcon = submitBtn.querySelector<HTMLElement>('[data-icon-default]');

    const supabase = createClient(ctx.supabaseUrl, ctx.supabaseAnonKey, {
      auth: { persistSession: false, autoRefreshToken: false },
    });

    function setMsg(text: string, kind: 'info' | 'error' | 'ok' = 'info'): void {
      msgEl.textContent = text;
      msgEl.className = `kl-login-msg is-${kind}`;
    }
    function setLoading(loading: boolean): void {
      submitBtn.disabled = loading;
      if (submitIcon) {
        submitIcon.className = loading
          ? 'v-icon fa-light fa-spinner-third fa-spin'
          : 'v-icon fa-light fa-right-to-bracket';
      }
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const email = String(formData.get('email') ?? '').trim();
      const password = String(formData.get('password') ?? '');
      if (!email || !password) {
        setMsg('E-Mail und Passwort erforderlich.', 'error');
        return;
      }

      setLoading(true);
      setMsg('Anmelden …');

      const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authErr || !authData.session) {
        setLoading(false);
        setMsg(`Login fehlgeschlagen: ${authErr?.message ?? 'unbekannt'}`, 'error');
        return;
      }

      setMsg('Lade Tenant-Session …');

      let bridgeBody: DevBridgeResponse;
      try {
        const res = await fetch(
          `${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/dev-tenant-bridge`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authData.session.access_token}`,
              apikey: ctx.supabaseAnonKey,
            },
            body: '{}',
          },
        );
        bridgeBody = (await res.json()) as DevBridgeResponse;
        if (!res.ok || bridgeBody.error) {
          setLoading(false);
          const msg = bridgeBody.error?.message ?? `HTTP ${res.status}`;
          setMsg(`Bridge-Fehler: ${msg}`, 'error');
          return;
        }
      } catch (err) {
        setLoading(false);
        setMsg(`Netzwerk-Fehler: ${(err as Error).message}`, 'error');
        return;
      }

      setMsg(`Verbunden — Tenant ${bridgeBody.tenantId.slice(0, 8)}…`, 'ok');

      const session: AuthBridgeSession = {
        token: bridgeBody.token,
        expiresAt: bridgeBody.expiresAt,
        tenantId: bridgeBody.tenantId,
        erpConnectionId: bridgeBody.erpConnectionId ?? 'standalone',
        marketplaceKind: bridgeBody.marketplaceKind,
        isNewTenant: false,
      };
      resolve(session);
    });
  });
}
